import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Play, Headphones } from "lucide-react";

const fmt = (s) => {
  if (!s || s < 0) return "0:00";
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60);
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}` : `${m}:${String(sec).padStart(2, "0")}`;
};

const sortStreams = (a, b) => {
  const at = a.progress?.updated_at ? new Date(a.progress.updated_at).getTime() : 0;
  const bt = b.progress?.updated_at ? new Date(b.progress.updated_at).getTime() : 0;
  return bt - at;
};

/**
 * ContinueListeningRail — Spotify-style shelf of owned streams.
 * Renders nothing when logged-out or when the buyer owns no streams.
 */
export default function ContinueListeningRail() {
  const { user, loading } = useAuth();
  const [streams, setStreams] = useState(null);

  useEffect(() => {
    if (loading || !user) { setStreams([]); return; }
    api.get("/buyer/streams")
      .then(({ data }) => setStreams(data))
      .catch(() => setStreams([]));
  }, [user, loading]);

  if (!user || !streams || streams.length === 0) return null;

  const rail = [...streams].sort(sortStreams).slice(0, 6);

  return (
    <section className="border-b-2 border-[#1F3D2A] bg-[#1F3D2A] text-white" data-testid="continue-listening-rail">
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C] flex items-center gap-2">
              <Headphones className="w-3 h-3" /> / Continue listening
            </div>
            <h2 className="heading text-2xl sm:text-3xl font-black uppercase tracking-tight mt-1">Your streams, right where you left off</h2>
          </div>
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-white/60">{streams.length} in your library</div>
        </div>
        <div className="flex gap-4 overflow-x-auto pb-2 -mx-6 px-6 snap-x snap-mandatory" data-testid="continue-listening-scroll">
          {rail.map((s) => {
            const pos = s.progress?.position_seconds || 0;
            const total = s.total_duration_seconds || 0;
            const started = pos > 5;
            const pct = total > 0 ? Math.min(100, Math.round((pos / total) * 100)) : 0;
            return (
              <Link
                key={s.product_id}
                to={`/stream/${s.product_id}`}
                data-testid={`continue-card-${s.product_id}`}
                className="group flex-shrink-0 w-[260px] snap-start brut-border bg-[#EDE5D2] text-[#1F3D2A] hover:brut-shadow-accent transition-all"
              >
                <div className="aspect-square bg-[#0e2118] relative overflow-hidden">
                  {s.image_url ? (
                    <img src={s.image_url} alt={s.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  ) : (
                    <div className="w-full h-full grid place-items-center">
                      <Headphones className="w-16 h-16 text-[#C77E3C] opacity-60" />
                    </div>
                  )}
                  <div className="absolute top-2 left-2 brut-border bg-[#C77E3C] text-white px-2 py-0.5 mono text-[9px] uppercase tracking-[0.2em]">{s.stream_type}</div>
                  <div className="absolute inset-x-0 bottom-0 bg-black/70 h-1">
                    <div className="h-full bg-[#C77E3C]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <div className="p-3 border-t-2 border-[#1F3D2A]">
                  <div className="heading font-bold text-base leading-tight line-clamp-2">{s.title}</div>
                  <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#555] mt-1 truncate">by {s.seller_name}</div>
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <div className="mono text-[9px] uppercase tracking-[0.2em] text-[#1F3D2A]">
                      {started ? (
                        <>Ch. {(s.progress.chapter_index || 0) + 1} · {fmt(pos)}</>
                      ) : (
                        <>New · {fmt(total)}</>
                      )}
                    </div>
                    <div className="brut-border bg-[#C77E3C] text-white px-2 py-1 mono text-[9px] uppercase tracking-[0.2em] flex items-center gap-1 group-hover:bg-[#1F3D2A]">
                      <Play className="w-3 h-3" /> {started ? "Resume" : "Play"}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
